import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import * as moment from 'jalali-moment';
import {Moment} from 'jalali-moment';
import {FormControl} from '@angular/forms';
import {FormValidationService} from '../../services/form-validation/form-validation.service';
import {DateUtil} from '../../utils/date.util';
import {Subject} from 'rxjs';

type Type = 'gregorian' | 'shamsi';

export interface IError {
    message: string;
}

export interface IConfig {
    /**
     * Uses `jalali-moment.js` supported formats. i.e. both YYYY-MM-DD and jYYYY-jMM-jDD are supported.
     */
    format: string;
    /**
     * Can be `outline` for outline based form fields, or none for the plain material.
     */
    appearance: string;
    validation?: {
        [lang: string]: {
            message: string;
            required: string;
        }
    };
}

@Component({
    selector: 'npa-date',
    templateUrl: './npa-date.component.html',
    styleUrls: ['./npa-date.component.styl']
})
export class NpaDateComponent implements OnInit, OnChanges {
    @Input() label: string;
    @Input() lang: string;
    @Input() valueChange;
    @Input() required = false;
    @Input() readonly: boolean;
    dateValue: string = undefined;

    @Input() type: Type = 'shamsi';
    @Input() config: IConfig = {
        format: 'YYYY-MM-DD',
        appearance: 'outline'
    };

    @Input() isTypeNotVisible: boolean;
    validation: any = {
        format: {
            prs: 'تاریخ باید به فارمت YYYY-MM-DD باشد',
            en: 'Please follow the format YYYY-MM-DD',
            ps: 'نیټه باید په YYYY-MM-DD فارمت وی.'
        },
        required: {
            prs: 'این معلومات الزامی است',
            en: 'This information is required',
            ps: 'دغه مالومات اړین ده'
        },
        value: {
            prs: 'تاریخ وارد شده اشتباه میباشد',
            en: 'The entered date is incorrect',
            ps: 'تاریخ وارد شده اشتباه میباشد'
        }
    };

    @ViewChild('yearInput', {static: false}) yearInput: ElementRef;
    @ViewChild('monthInput', {static: false}) monthInput: ElementRef;
    @ViewChild('dayInput', {static: false}) dayInput: ElementRef;

    @Input() model: FormControl;
    @Input() invalidate = new Subject();
    @Output() modelChange: EventEmitter<Date | any> = new EventEmitter<Date | any>();
    error: IError = {message: undefined};

    isDirty: boolean;
    isFocused: boolean;

    yearValue = '';
    dayValue: string;
    monthValue: string;

    constructor(
        public formValidationService: FormValidationService
    ) {
        this.invalidate.subscribe((options: {shouldInvalidate: boolean, message?: string}) => {
            this.error = options && options.shouldInvalidate ?
                {message: options.message !== undefined ? options.message : this.validation.value[this.lang]} : undefined;
            if (options.shouldInvalidate) {
                this.model.setErrors({invalid: true});
            } else {
                this.model.setErrors(null);
            }
        });
    }

    ngOnInit(): void {
        if (this.type === 'shamsi') {
            this.config.format = 'jYYYY-jMM-jDD';
        } else {
            this.config.format = this.config.format || 'YYYY-MM-jDD';
        }
        this.initReadOnlyState();
    }

    setDateInputFocus(element: ElementRef | any) {
        this.isFocused = true;
        if (element && element.nativeElement) {
            element.nativeElement.focus();
        } else if (element) {
            element.focus();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (this.model && this.model.value === undefined && this.model.value === '') {
            return;
        }
        if (changes.valueChange && changes.valueChange.currentValue) {
            const enteredDate = changes.valueChange.currentValue;
            const date = new Date(enteredDate);
            const dateMoment = moment(DateUtil.convert(date));
            if (this.type === 'shamsi') {
                dateMoment.format('jYYYY-jMM-jDD');
                this.setDate(dateMoment, this.type);
                this.updateDateValue();
            } else {
                dateMoment.format('YYYY-MM-DD');
                this.setDate(dateMoment, this.type);
                this.updateDateValue();
            }
        }
    }

    dayInputChange($event) {
        const DIGITS = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
        if ($event.key === 'Tab' || $event.key === ':') {
            this.dayInput.nativeElement.focus();
        }
        if ($event.key === 'Backspace') {
            this.dayValue = this.dayValue.slice(0, -1);
        }
        if (DIGITS.indexOf($event.key) === -1) {
            return;
        }
        const dateType = this.type;
        const day = $event.key;
        let daysInMonth;
        const INITIAL_DIGIT = '0';
        if (dateType === 'shamsi') {
            daysInMonth = moment(this.yearValue + '-' + this.monthValue, 'jYYYY-jM').daysInMonth();
        } else {
            daysInMonth = moment(this.yearValue + '-' + this.monthValue, 'YYYY-MM').daysInMonth();
        }
        if (this.dayValue && this.dayValue.length > 1) {
            this.dayValue = this.dayValue.slice(1) + day;
        } else {
            this.dayValue = INITIAL_DIGIT + day;
        }
        if (+this.dayValue > daysInMonth) {
            this.error = {message: this.validation.value[this.lang]};
        } else {
            this.error = undefined;
        }
        this.updateDateValue();
        this.markInputDirty();
        return false;
    }

    monthInputChange($event) {
        const DIGITS = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
        if ($event.key === 'Tab' || $event.key === ':') {
            this.monthInput.nativeElement.focus();
        }
        if ($event.key === 'Backspace') {
            this.monthValue = this.monthValue.slice(0, -1);
        }
        if (DIGITS.indexOf($event.key) === -1) {
            return;
        }
        const INITIAL_DIGIT = '0';
        const month = $event.key;
        if (this.monthValue && this.monthValue.length > 1) {
            this.monthValue = this.monthValue.slice(1) + month;
        } else {
            this.monthValue = INITIAL_DIGIT + month;
        }
        this.updateDateValue();
        this.markInputDirty();
        return false;
    }

    yearInputChange($event) {
        const DIGITS = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
        if ($event.key === 'Tab' || $event.key === ':') {
            this.yearInput.nativeElement.focus();
        }
        if ($event.key === 'Backspace') {
            this.yearValue = this.yearValue.slice(0, -1);
        }
        if (DIGITS.indexOf($event.key) === -1) {
            return;
        }
        const year = $event.key;
        if (this.yearValue && this.yearValue.length >= 4) {
            this.yearValue = this.yearValue.slice(1) + year;
        } else {
            this.yearValue += year;
        }
        this.updateDateValue();
        this.markInputDirty();
        return false;
    }

    private isValid(value: string) {
        return /^(19|20|13|14)\d\d([-])(0[1-9]|[1-9]|1[012])([-])(0[1-9]|[1-9]|[12][0-9]|3[01])$/i.test(value);
    }

    onChange() {
        const dateValue = this.dateValue;
        try {
            if ((this.type === 'shamsi' && (+this.yearValue >= 1500 || +this.yearValue <= 1300))
                || (this.type === 'gregorian' && (+this.yearValue >= 3000 || +this.yearValue <= 1900))
                || +this.monthValue > 12
                || +this.dayValue > 31
            ) {
                this.error = {message: this.validation.value[this.lang]};
                return;
            }
            const dateMoment = moment(dateValue, this.config.format);
            if (!this.isValid(dateValue)) {
                this.error = {message: this.validation.value[this.lang]};
                return;
            }
            if (!dateMoment.isValid()) {
                this.error = {message: this.validation.format[this.lang]};
                return;
            }
            this.error = undefined;
            if (dateMoment.isValid() && dateValue.length === 10) {
                const gregDate = moment(DateUtil.convert(dateMoment.toDate())).format('YYYY-MM-DD');
                this.model.setValue(gregDate);
                this.modelChange.emit(this.model);
            } else {
                this.model.setValue(undefined);
                this.modelChange.emit(this.model);
            }
        } catch (error) {
            console.error(error);
        }
    }

    changeType(typeSlug: Type) {
        if (typeSlug === this.type) {
            return;
        }
        this.type = typeSlug;
        if (this.dateValue) {
            if (this.type === 'shamsi') {
                const dateMoment = moment(DateUtil.convert((new Date(this.dateValue))));
                this.dateValue = moment(DateUtil.convert((new Date(this.dateValue)))).format('jYYYY-jMM-jDD');
                this.setDate(dateMoment, this.type);
                this.updateDateValue();
            } else {
                const dateMoment = moment(this.dateValue, 'jYYYY-jMM-jDD');
                this.dateValue = moment(DateUtil.convert(dateMoment.toDate())).format('YYYY-MM-DD');
                const d = moment(DateUtil.convert(dateMoment.toDate()));
                this.setDate(d, this.type);
                this.updateDateValue();
            }
        }
        this.changeFormat();
    }

    private changeFormat() {
        if (this.type === 'shamsi') {
            this.config.format = 'jYYYY-jM-jD';
        } else {
            this.config.format = 'YYYY-M-D';
        }
    }

    private initReadOnlyState() {
        this.readonly = this.model.disabled || this.readonly;
    }

    private markInputDirty() {
        if (this.yearInput.nativeElement.value !== this.yearInput.nativeElement.defaultValue
            || this.monthInput.nativeElement.value !== this.monthInput.nativeElement.defaultValue
            || this.dayInput.nativeElement.value !== this.dayInput.nativeElement.defaultValue
        ) {
            this.isDirty = true;
        }
    }

    private updateDateValue() {
        this.dateValue = this.yearValue + '-' + this.monthValue + '-' + this.dayValue;
    }

    setDate(dateMoment: Moment, type: Type) {
        const year = (type === 'shamsi' ? dateMoment.jYear() : dateMoment.year());
        const month = ((type === 'shamsi' ? dateMoment.jMonth() : dateMoment.month()) + 1);
        const day = (type === 'shamsi' ? dateMoment.jDate() : dateMoment.date());
        this.yearValue = year.toString();
        this.monthValue = (month < 10 ? '0' + month : month).toString();
        this.dayValue = (day < 10 ? '0' + day : day).toString();
    }
}
